import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { DataGrid } from "@mui/x-data-grid";
import { esES } from "@mui/x-data-grid/locales";
import {
  Button,
  Link,
  Stack,
  Menu,
  MenuItem,
  ListItemIcon,
  Box,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Calendar,
  Files,
  UploadSimple,
  UserCircle,
  Pulse,
  SealCheck,
  PencilSimpleLine,
  ReadCvLogo,
} from "@phosphor-icons/react";
import { HeaderMainTitle } from "../Common/HeaderMainTitle";
import UploadDocumentDialog from "../../ClientDetails/Dialogs/UploadDocumentDialog";
import { useGetClientDocuments } from "hooks/useGetClientDocuments";
import { format } from "date-fns";
import { StaticDateRangePicker } from "@mui/x-date-pickers-pro/StaticDateRangePicker";
import actions from "../../../actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation } from "react-query";
import { unverifyClientDocument, updateDocument } from "api/customers";
import { uploadUserDocumentToS3Async } from "api/document";
import useVerifyClientDocument from "hooks/api/documents/useVerifyClientDocument";
import {
  NoDocumentsWithPermissionOverlay,
  ANNoDataOverlay,
  ANNoResultsOverlay,
  ANTableFilters,
} from "@answersnow/pkg-theme";
import * as selectors from "selectors";
import noDocumentSvg from "../../../assets/no_document.svg";
import { v4 as uuidv4 } from "uuid";
import { useGlobalToast } from "components/GlobalToastProvider";
import RecommendedTherapyHoursDialog from "components/ClientDetails/Dialogs/RecommendedTherapyHoursDialog";
import { useLocation } from "react-router-dom";
import {
  getClientId,
  translateDocumentType,
  translateDocumentStatus,
  translateCountObjectDisplayNames,
} from "../utils";

interface DrawerDocumentsProps {
  onBack: () => void;
  user: any;
}

export const DrawerDocuments: React.FC<DrawerDocumentsProps> = ({ onBack, user }) => {
  const { t, i18n } = useTranslation(["clientHeader"]);
  const dataGridLocaleText = i18n.language?.startsWith("es")
    ? esES.components.MuiDataGrid.defaultProps.localeText
    : undefined;
  const theme = useTheme();
  const dispatch = useDispatch();
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });
  const [uploadDocumentOpen, setUploadDocumentOpen] = useState(false);
  const [isNewDocument, setIsNewDocument] = useState(true);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [orderBy, setOrderBy] = useState("uploaded-date-desc");
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [uploadedByFilter, setUploadedByFilter] = useState<string[]>([]);
  const [uploadedDateFilter, setUploadedDateFilter] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [dropzoneFiles, setDropzoneFiles] = useState<any[]>([]);
  const [alreadyUploadedFiles, setAlreadyUploadedFiles] = useState<any[]>([]);
  const [uploadDocType, setUploadDocType] = useState("");
  const [statusFilter, setStatusFilter] = useState<string[]>([]);

  const uploadDocumentLoading = useSelector((state: any) =>
    selectors.getuploadDocumentLoading(state)
  );
  const userId = useSelector((state: any) => selectors.getUserId(state));
  const customerDetails = useSelector((state: any) => selectors.getCustomerDetails(state));
  const currentUser = useSelector((state: any) => selectors.getUser(state));
  const userPermissions =
    useSelector((state: any) => selectors.getUserPermissionsList(state)) || {};
  const documentTypes = useSelector((state: any) => selectors.getDocumentTypes(state)) || [];

  const [optionsMenuAnchorEl, setOptionsMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [recommendedTherapyHoursOpen, setRecommendedTherapyHoursOpen] = useState(false);

  const workflowStatus = customerDetails?.details?.workflow_status;

  const { search } = useLocation();
  const clientId = getClientId(user);

  useEffect(() => {
    const query = new URLSearchParams(search);
    const documentType = query.get("uploadDocType");
    if (documentType && documentTypes.find((doc: any) => doc.name === documentType)) {
      setUploadDocType(documentType);
      setUploadDocumentOpen(true);
    }
  }, [search, documentTypes]);

  const { showToast } = useGlobalToast();

  const { mutate: updateClientDocument, isLoading: isLoadingDocumentUpdate } = useMutation(
    (documentData: any) => updateDocument(clientId, documentData),
    {
      onSuccess: () => updateTable(),
      onError: (error) => {
        console.error("Error updating document:", error);
      },
    }
  );

  const { verifyDocument, verifyIsLoading } = useVerifyClientDocument({
    onSuccess: () => updateTable(),
  });

  const { mutate: unverifyDocument, isLoading: unverifyIsLoading } = useMutation(
    (documentId: string) => unverifyClientDocument(documentId),
    {
      onSuccess: () => updateTable(),
      onError: (error, documentId) => {
        showToast({
          message: "Whoops! Something went wrong",
          errorState: true,
          retryHandler: () => unverifyDocument(documentId),
        });
        console.error("Error unverifying document:", error);
      },
    }
  );

  useEffect(() => {
    if (!uploadDocumentLoading) {
      updateTable();
      const isTreatmentPlan = uploadDocType === "TREATMENT_PLAN";
      if (workflowStatus !== "FULL_CUSTOMER" && isTreatmentPlan) {
        setRecommendedTherapyHoursOpen(true);
      }
    }
  }, [uploadDocumentLoading]);

  const viewMode = userPermissions["view_client_dashboard"];
  const orderByMap: any = {
    "uploaded-date-asc": { sort: "created", direction: "asc" },
    "uploaded-date-desc": { sort: "created", direction: "desc" },
    type: { sort: "display_name", direction: "asc" },
    "uploaded-by": { sort: "uploaded_by", direction: "asc" },
  };

  const {
    isFetching,
    data: response,
    refetch: updateTable,
  } = useGetClientDocuments(clientId, {
    sort: orderByMap[orderBy]?.sort,
    direction: orderByMap[orderBy]?.direction,
    page: paginationModel.page,
    size: paginationModel.pageSize,
    typeList: !typeFilter.length ? null : typeFilter.join(","),
    uploadedByList: uploadedByFilter.join(","),
    startDate: uploadedDateFilter?.[0],
    endDate: uploadedDateFilter?.[1],
    status: statusFilter.join(","),
  });

  const debounce = (fn: (...args: any[]) => any, delay: number) => {
    let timeout: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), delay);
    };
  };

  const debouncedUpdateTable = debounce(updateTable, 300);

  const prevFiltersRef = useRef({
    typeFilter: typeFilter,
    uploadedByFilter: uploadedByFilter,
    uploadedDateFilter: uploadedDateFilter,
    statusFilter: statusFilter,
  });

  useEffect(() => {
    const prevFilters = prevFiltersRef.current;

    if (
      !arraysEqual(prevFilters.typeFilter, typeFilter) ||
      !arraysEqual(prevFilters.uploadedByFilter, uploadedByFilter) ||
      !arraysEqual(prevFilters.uploadedDateFilter, uploadedDateFilter) ||
      !arraysEqual(prevFilters.statusFilter, statusFilter)
    ) {
      setPaginationModel((prevPaginationModel) => ({
        ...prevPaginationModel,
        page: 0,
      }));
    }
    prevFiltersRef.current = {
      typeFilter: typeFilter,
      uploadedByFilter: uploadedByFilter,
      uploadedDateFilter: uploadedDateFilter,
      statusFilter: statusFilter,
    };

    debouncedUpdateTable();
  }, [typeFilter, uploadedByFilter, uploadedDateFilter, statusFilter]);

  const arraysEqual = (a: any, b: any) => {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (a.length !== b.length) return false;

    for (let i = 0; i < a.length; ++i) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  };

  const documents = response?.data.documents;
  // The API returns count as a string; DataGrid's rowCount (and the
  // TablePagination it renders) require a number, and `!"0"` is falsy-unsafe
  // for the hideFooter check below.
  const rowCount = Number(response?.data.count) || 0;
  const typeCount = translateCountObjectDisplayNames(
    t,
    response?.data.type_count,
    translateDocumentType
  );
  const uploadedByCount = response?.data.uploaded_by_count;
  const statusCount = translateCountObjectDisplayNames(
    t,
    response?.data.status_count,
    translateDocumentStatus
  );

  const handleFileUpload = (file: any) => {
    setDropzoneFiles([
      { errors: !!file.fileRejections?.[0], file: file.acceptedFiles[0] || file.fileRejections[0] },
    ]);
    if (!uploadDocumentOpen) setUploadDocumentOpen(true);
  };

  const handleFileDelete = (file: any) => {
    setDropzoneFiles(dropzoneFiles.filter((dropzoneFile) => dropzoneFile !== file));
  };

  const handleLoadedFileDelete = (file: any) => {
    setAlreadyUploadedFiles(alreadyUploadedFiles.filter((uploadedFile) => uploadedFile !== file));
  };

  const handleOptionsMenuClose = () => {
    setSelectedRow(null);
    setOptionsMenuAnchorEl(null);
  };

  const isOptionsMenuOpen = Boolean(optionsMenuAnchorEl);

  const handleVerifyDocument = () => {
    setOptionsMenuAnchorEl(null);
    verifyDocument(selectedRow.id);
  };

  const handleUnverifyDocument = () => {
    setOptionsMenuAnchorEl(null);
    unverifyDocument(selectedRow.id);
  };

  const handleEdit = () => {
    const { row } = selectedRow;
    setUploadDocumentOpen(true);
    setIsNewDocument(false);
    setAlreadyUploadedFiles([{ name: row.name, key: row.name, size: row.file_size ?? 0 }]);
    setUploadDocType(documentTypes.find((docType: any) => docType.display_name === row.type).name);
    setOptionsMenuAnchorEl(null);
  };

  const submitDocumentUpload = async (uploadDocType: string) => {
    const uploadDoc = dropzoneFiles[0]?.file;
    const { details } = customerDetails;
    let upload;
    if (uploadDoc) {
      const fileUUID = uuidv4();
      const [fileName, fileExtension] = uploadDoc.name.split(".");
      const uploadKey = isNewDocument
        ? `${details.cognito_id}/${fileName}-${fileUUID}.${fileExtension}`
        : selectedRow?.row?.key;
      const response = await uploadUserDocumentToS3Async({
        file: uploadDoc,
        key: uploadKey,
        type: "USER_DOC",
        contentType: uploadDoc.type,
        acl: "authenticated-read",
      });
      upload = response.data;
    }
    if (isNewDocument) {
      const document = {
        filename: upload.Key.split("/")[1],
        key: upload.Key,
        display_filename: uploadDoc.name,
        clientId: clientId,
        type: uploadDocType,
        uploaded_by: currentUser.id,
        file_size: uploadDoc.size,
        clinicianUserId: currentUser.id,
      };
      dispatch(actions.uploadCustomerDocument(document));
    } else {
      const documentData = {
        id: selectedRow.id,
        type: uploadDocType,
        filename: uploadDoc ? upload.Key.split("/")[1] : selectedRow?.row?.name,
        display_filename: uploadDoc ? uploadDoc.name : selectedRow?.row?.name,
        file_size: uploadDoc ? uploadDoc.size : selectedRow?.row?.file_size,
        uploaded_by: uploadDoc ? currentUser.id : null,
      };
      updateClientDocument(documentData);
    }
  };

  const generateRows = () => {
    if (!documents) return [];
    return documents.map((document: any) => ({
      id: document.id,
      name: document.display_filename || document.filename,
      type: document.display_name,
      "uploaded-by": document.uploaded_by,
      "uploaded-date": document.created,
      verified: document.verified,
      edit: "Edit",
      file_size: document.file_size,
      key: document.key,
    }));
  };
  const rows = generateRows();

  const getColumnDefinitions = (handleOpenFile: (params: any) => void) => {
    const columns: any[] = [
      {
        field: "name",
        headerName: t("clientHeader:documents.name"),
        flex: 2,
        sortable: false,
        renderCell: (params: any) => {
          return (
            <Link
              color="primary"
              variant="body2"
              underline="hover"
              onClick={() => handleOpenFile(params)}
              sx={{ "&:hover": { cursor: "pointer" } }}
            >
              {params.value}
            </Link>
          );
        },
      },
      {
        field: "type",
        headerName: t("clientHeader:documents.type"),
        flex: 1,
        sortable: false,
        renderCell: (params: any) => translateDocumentType(t, params.value),
      },
    ];
    return columns;
  };

  const orderingMenuItems = [
    {
      label: t("clientHeader:documents.type"),
      value: "type",
      icon: <Files weight="duotone" />,
    },
    {
      label: t("clientHeader:documents.uploadedBy"),
      value: "uploaded-by",
      icon: <UserCircle weight="duotone" />,
    },
    {
      label: t("clientHeader:documents.uploadedDate"),
      value: "uploaded-date-asc",
      icon: <Calendar weight="duotone" />,
      caption: t("clientHeader:documents.ascending"),
    },
    {
      label: t("clientHeader:documents.uploadedDate"),
      value: "uploaded-date-desc",
      icon: <Calendar weight="duotone" />,
      caption: t("clientHeader:documents.descending"),
    },
  ];

  const filtersMenuItems = [
    {
      label: t("clientHeader:documents.type"),
      value: "type",
      icon: <Files weight="duotone" />,
      countObject: typeCount,
      state: typeFilter,
      setState: setTypeFilter,
    },
    {
      label: t("clientHeader:documents.uploadedDate"),
      value: "uploaded-date",
      icon: <Calendar weight="duotone" />,
      customComponent: uploadedDateFilter[0] ? (
        <StaticDateRangePicker
          value={uploadedDateFilter}
          onChange={(date: any) => setUploadedDateFilter(date)}
          slotProps={{ actionBar: { actions: [] }, toolbar: { hidden: true } }}
          calendars={2}
        />
      ) : (
        <StaticDateRangePicker
          value={[null, null]}
          onChange={(date: any) => setUploadedDateFilter(date)}
          slotProps={{ actionBar: { actions: [] }, toolbar: { hidden: true } }}
          calendars={2}
        />
      ),
      filterChipProps: uploadedDateFilter[0]
        ? {
            label: `${t("clientHeader:documents.uploadedDateIs")} : ${format(new Date(uploadedDateFilter[0]), "MM/dd/yy")}
          ${
            uploadedDateFilter[1] ? ` - ${format(new Date(uploadedDateFilter[1]), "MM/dd/yy")}` : ""
          }`,
            onDelete: () => setUploadedDateFilter([null, null]),
          }
        : undefined,
      showChipConditionCustomComponent: !!uploadedDateFilter[0],
    },
    {
      label: t("clientHeader:documents.uploadedBy"),
      value: "uploaded-by",
      icon: <UserCircle weight="duotone" />,
      countObject: uploadedByCount,
      state: uploadedByFilter,
      setState: setUploadedByFilter,
    },
    {
      label: t("clientHeader:documents.status"),
      value: "verified",
      icon: <Pulse weight="duotone" />,
      countObject: statusCount,
      state: statusFilter,
      setState: setStatusFilter,
    },
  ];

  const handleClearFilters = () => {
    setTypeFilter([]);
    setUploadedByFilter([]);
    setUploadedDateFilter([null, null]);
    setStatusFilter([]);
  };

  const customNoRowsOverlay: any = {
    NoDocuments: {
      component: ANNoDataOverlay,
      height: "296px",
      props: {
        imgSrc: noDocumentSvg,
        title: t("clientHeader:documents.noDocumentsYet"),
        description: t("clientHeader:documents.noDocumentsDescription"),
      },
    },
    NoDocumentsWithPermission: {
      component: NoDocumentsWithPermissionOverlay,
      height: "296px",
      props: { handleFileUpload },
    },
    NoResults: {
      component: ANNoResultsOverlay,
      height: "236px",
      props: { handleClearFilters },
    },
  };

  const overlayName =
    !typeFilter.length &&
    !uploadedByFilter.length &&
    !uploadedDateFilter[0] &&
    !uploadedDateFilter[1]
      ? viewMode
        ? "NoDocuments"
        : "NoDocumentsWithPermission"
      : "NoResults";

  const renderContent = () => {
    if (!clientId) {
      return (
        <Box
          sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "calc(100vh - 120px)" }}>
        <Stack
          direction="row"
          paddingY={theme.spacing(5)}
          paddingX="24px"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <ANTableFilters
            orderingMenuItems={orderingMenuItems}
            orderBy={orderBy}
            setOrderBy={setOrderBy}
            filtersMenuItems={filtersMenuItems}
            filtersButtonProps={{ disabled: documents?.length === 0 }}
            language={i18n.language?.startsWith("es") ? "es" : "en"}
          />
          {!viewMode && (
            <Button
              color="secondary"
              startIcon={<UploadSimple />}
              size="small"
              onClick={(event) => {
                event.currentTarget.blur();
                setUploadDocumentOpen(true);
              }}
              sx={{ minWidth: "fit-content" }}
            >
              {t("clientHeader:documents.uploadDocument")}
            </Button>
          )}
        </Stack>

        <Box sx={{ flex: 1, minHeight: 0 }}>
          <DataGrid
            rows={rows}
            localeText={dataGridLocaleText}
            loading={
              (isFetching && !documents) ||
              isLoadingDocumentUpdate ||
              uploadDocumentLoading ||
              verifyIsLoading ||
              unverifyIsLoading
            }
            pageSizeOptions={[5, 10, 20]}
            columns={getColumnDefinitions(({ row }: any) => {
              const url = `/document-viewer/${userId}/${row.id}`;
              window.open(url, "_blank", "noopener,noreferrer");
            })}
            disableRowSelectionOnClick
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            slots={{
              noRowsOverlay: customNoRowsOverlay[overlayName].component,
            }}
            slotProps={{ noRowsOverlay: { ...customNoRowsOverlay[overlayName].props } }}
            sx={{
              "--DataGrid-overlayHeight": customNoRowsOverlay[overlayName].height,
              width: "100%",
              "& .MuiDataGrid-columnHeader:first-of-type": {
                paddingLeft: "24px",
              },
              "& .MuiDataGrid-columnHeader:last-of-type": {
                paddingRight: "24px",
              },
              "& .MuiDataGrid-cell:first-of-type": {
                paddingLeft: "24px",
              },
              "& .MuiDataGrid-cell:last-of-type": {
                paddingRight: "24px",
              },
              "& .MuiDataGrid-footerContainer": {
                paddingLeft: "24px",
                paddingRight: "24px",
              },
            }}
            hideFooter={!rowCount}
            paginationMode="server"
            rowCount={rowCount}
          />
        </Box>
      </Box>
    );
  };

  return (
    <>
      <HeaderMainTitle user={user} title={t("clientHeader:documents.title")} onBack={onBack} icon={ReadCvLogo} />

      {renderContent()}

      <UploadDocumentDialog
        open={uploadDocumentOpen}
        handleClose={() => {
          setUploadDocumentOpen(false);
          setSelectedRow(null);
          setUploadDocType("");
          setTimeout(() => {
            setIsNewDocument(true);
            setAlreadyUploadedFiles([]);
            setDropzoneFiles([]);
          }, 250);
        }}
        handleFileUpload={handleFileUpload}
        dropzoneFiles={dropzoneFiles}
        handleFileDelete={handleFileDelete}
        handleLoadedFileDelete={handleLoadedFileDelete}
        submitDocumentUpload={submitDocumentUpload}
        isNewDocument={isNewDocument}
        alreadyUploadedFiles={alreadyUploadedFiles}
        uploadDocType={uploadDocType}
        setUploadDocType={setUploadDocType}
      />

      <RecommendedTherapyHoursDialog
        clientId={clientId}
        onClose={() => setRecommendedTherapyHoursOpen(false)}
        open={recommendedTherapyHoursOpen}
      />

      <Menu
        anchorEl={optionsMenuAnchorEl}
        open={isOptionsMenuOpen}
        onClose={handleOptionsMenuClose}
      >
        {selectedRow?.row?.verified ? (
          <MenuItem
            key={`${selectedRow?.id}_verify_document`}
            disabled={!userPermissions.verify_client_document}
            onClick={handleUnverifyDocument}
          >
            <ListItemIcon>{<SealCheck weight="duotone" />}</ListItemIcon>
            {t("clientHeader:documents.cancelVerification")}
          </MenuItem>
        ) : (
          <MenuItem
            key={`${selectedRow?.id}_verify_document`}
            disabled={!userPermissions.verify_client_document}
            onClick={handleVerifyDocument}
          >
            <ListItemIcon>{<SealCheck weight="duotone" />}</ListItemIcon>
            {t("clientHeader:documents.verifyDocument")}
          </MenuItem>
        )}
        <MenuItem key={`${selectedRow?.id}_handle_edit`} onClick={handleEdit}>
          <ListItemIcon>{<PencilSimpleLine weight="duotone" />}</ListItemIcon>
          {t("clientHeader:documents.edit")}
        </MenuItem>
      </Menu>
    </>
  );
};
